import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { compressImageToDataUri } from "@/utils/imageCompress";

/**
 * Accepts any common image, downscales/recompresses it client-side to a
 * PNG/JPEG under 200 KB, and returns it as a base64 data URI via onAdd.
 * The server re-validates by magic byte + size.
 */
export function LogoUploader({ onAdd, disabled }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    setError("");
    if (!file) return;
    setBusy(true);
    try {
      const dataUri = await compressImageToDataUri(file);
      onAdd(dataUri);
    } catch (err) {
      setError(err.message || "Could not process that image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = ""; // allow re-selecting the same file
        }}
      />
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {busy ? "Optimizing…" : "Add logo"}
      </button>
      <p className="text-xs text-muted-foreground">
        Any image works - we automatically resize it to fit.
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
